/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2014, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./UrlWidget',
	'../../../app/nls/StringResources',
	'./media/MediaResolver',
	'jquery',
	'underscore'
], function (UrlWidget, resources, MediaResolver, $, _) {

	const MediaWidget = UrlWidget.extend({
		init: function (options) {
			MediaWidget.inherited('init', this, arguments);

			this.api = {
				getId: this.getId.bind(this),
				setMediaUrl: this.setUrl.bind(this)
			};

			this.model = this.model || {};
			this.model.mediaLink = options && options.initialConfigJSON && options.initialConfigJSON.mediaLink;
			this.propertyCallbacks = this.dashboardApi.getFeature('PropertyCallbacks');
		},

		onContainerReady: function () {
			MediaWidget.inherited('onContainerReady', this, arguments);
			if (this.model) {
				this.model.on('change:mediaLink', this._onMediaLinkChange, this);
				this.model.on('change:title', this._onPropertyChange, this);
				this.addWhiteListAttrs('mediaLink', 'mediaType', 'title');
			}

			this.eventRouter.trigger('media:ready', this.api);
		},

		render: function() {
			if (this.propertyCallbacks.validateMediaInput(null, this.model.mediaLink).isValid) {
				this.model.mediaLink = this.encodeURL(this.model.mediaLink);
				this.media = MediaResolver.loadFromModel(this.model);
				this.$el.append(this.getHtmlRender());
				if (this.media) {
					this.media.markupLoaded(this.$el, this.isAuthoringMode ? 'authoring' : 'consume');
				}
			} else {
				this.$el.append(UrlWidget.getDefaultConsumeMarkup(resources.get('mediaMissingUrl')));
			}
		},

		setUrl: function (url, errorData={}) {
			const ret = this.propertyCallbacks.validateMediaInput(null, url);
			errorData.message = ret.message;
			if (ret.isValid) {
				const transactionId = _.uniqueId('_addMedia_');
				this.set({
					mediaLink: url,
					mediaType: ret.media.type
				}, {
					sender: this,
					payloadData: {
						undoRedoTransactionId: transactionId
					}
				});
				this.updateModelContent();
				return true;
			}
			return false;
		},


		onAuthoringMode: function () {
			MediaWidget.inherited('onAuthoringMode', this, arguments);
			//Only prompt media to perform DOM operations when root DOM
			//reference is valid
			if (this.media && $.contains(document.body, this.el)) {
				this.media.showAuthoringMode();
			}
		},

		onConsumeMode: function () {
			MediaWidget.inherited('onConsumeMode', this, arguments);
			//Only prompt media to perform DOM operations when root DOM
			//reference is valid
			if (this.media && $.contains(document.body, this.el)) {
				this.media.showConsumeMode();
			}
		},

		onEnterContainer: function () {
			const mediaInput = this.$el.find('.inlineEditInput');
			mediaInput.focus();
		},

		isInlineEditMode: function () {
			return !this.model.mediaLink;
		},

		getInlineEditCaption: function () {
			return resources.get('mediaPasteLink');
		},

		getMissingUrlText: function () {
			return resources.get('mediaMissingUrl');
		},

		getAriaLabelText: function () {
			return resources.get('mediaAriaLabel');
		},

		getLabel: function () {
			return this._getTitle() || resources.get('mediaWidgetTitle');
		},

		getContentClass: function() {
			return 'mediaWidgetContent';
		},

		_getTitle: function () {
			return this.model && this.model.get ? this.model.get('title') : null;
		},

		_onMediaLinkChange: function (options) {
			delete this.model.mediaType;
			return this._onPropertyChange(options);
		},

		_onPropertyChange: function (options) {
			let updatedHtml = null;
			if (this.model.mediaLink) {
				this.model.mediaLink = this.encodeURL(this.model.mediaLink);
				if (this.propertyCallbacks.validateMediaInput(null, this.model.mediaLink).isValid) {
					this.media = MediaResolver.loadFromModel(this.model);
					updatedHtml = this.getHtmlRender();
					const staticContent = this.$el.find('.staticContent');
					staticContent.empty();
					staticContent.replaceWith(updatedHtml);
					this.media.markupLoaded(this.$el, this.isAuthoringMode ? 'authoring' : 'consume');
				}
			} else {
				this.renderInlineEditUi();
				updatedHtml = UrlWidget.getDefaultConsumeMarkup(resources.get('mediaMissingUrl'));
			}
			this.updateMarkup();
			if (options.data && options.data.undoRedoTransactionId) {
				this.updateModelContent(updatedHtml, options.data.undoRedoTransactionId);
			} else {
				this.updateModelContent(updatedHtml);
			}
		},

		getHtmlRender: function () {
			return this.media ? this.media.getMarkup(this._getMediaParams()) : '';
		},

		_getMediaParams: function () {
			const params = {};
			const title = this._getTitle();
			if (title) {
				params.title = title;
			}
			return params;
		}
	});

	MediaWidget.getDefaultSpec = function () {
		const spec = {
			model: {
				type: 'media',
				avatarHtml: UrlWidget.getDefaultConsumeMarkup(),
				title: ''
			},
			layoutProperties: {
				style: {
					width: '480px',
					height: '360px'
				}
			}
		};
		return Promise.resolve(spec);
	};

	return MediaWidget;
});
